import type { ReactElement, ReactNode } from 'react';

import type { Feature } from './features';
import { useEntitlement } from './useEntitlement';
import { UpsellTeaser } from './upsell/UpsellTeaser';

type Props = {
  feature: Feature;
  children: ReactNode;
  /**
   * Rendered on denial. Defaults to the upsell teaser — deliberately NOT `RouteNotFound`.
   *
   * A 404 tells an agency the capability does not exist. The entire premise of the
   * reduced tier is that it does exist and they can have it, so denial has to read as an
   * invitation rather than a dead end.
   */
  fallback?: ReactNode;
  /** The user's real panel, blurred behind the teaser. Omit unless it renders real data. */
  preview?: ReactNode;
  loading?: ReactNode;
};

/** The route/panel gate. Mirrors `<RequireRole>` in `src/Auth/rbac/`, on the plan axis. */
export function RequireFeature({ feature, children, fallback, preview, loading = null }: Props): ReactElement {
  const state = useEntitlement(feature);

  if (state === 'loading') return <>{loading}</>;
  if (state === 'granted') return <>{children}</>;
  return <>{fallback ?? <UpsellTeaser feature={feature} preview={preview} />}</>;
}

export default RequireFeature;
